import { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars3Icon, UserCircleIcon, ArrowRightOnRectangleIcon, ChevronDownIcon, KeyIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import Modal from '../common/Modal.jsx';
import apiClient from '../../services/api/apiClient.js';

/** Mismo patrón que los proyectos hermanos (Turkimex/GGv2, ver sus `Header.jsx`):
 * header blanco pegajoso, hamburguesa solo en móvil, dropdown de usuario con avatar
 * a la derecha. Portado 2026-09-28 junto con el resto del sistema de diseño. */
export default function Header({ onAbrirMenu, shouldShowPaymentWarning }) {
  const { user, logoutUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showUser, setShowUser] = useState(false);
  const userRef = useRef(null);

  // Cambio de contraseña propia -- V1: opción "Password" del menú de usuario (Header.js +
  // Password.jsx): Password Anterior / Password Nuevo / Ingresa Nuevamente.
  const [modalPassword, setModalPassword] = useState(false);
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNuevo, setPasswordNuevo] = useState('');
  const [passwordVerificar, setPasswordVerificar] = useState('');
  const [guardandoPassword, setGuardandoPassword] = useState(false);

  async function cambiarPassword(e) {
    e.preventDefault();
    if (passwordNuevo !== passwordVerificar) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'La nueva contraseña no coincide' });
      return;
    }
    setGuardandoPassword(true);
    try {
      await apiClient.patch('/usuarios/me/password', { password: passwordActual, passwordNuevo });
      setModalPassword(false);
      setPasswordActual(''); setPasswordNuevo(''); setPasswordVerificar('');
      Swal.fire('Good job!', 'Creado con exito', 'success');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message || 'Ocurrió un error inesperado' });
    } finally {
      setGuardandoPassword(false);
    }
  }

  useEffect(() => {
    function handle(e) {
      if (userRef.current && !userRef.current.contains(e.target)) setShowUser(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  function salir() {
    logoutUser();
    navigate('/login');
  }

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
      <button onClick={onAbrirMenu} className="btn-ghost p-2 lg:hidden">
        <Bars3Icon className="w-5 h-5" />
      </button>

      <div className="flex-1 flex items-center">
        {shouldShowPaymentWarning && (
          <span className="text-xs font-medium text-amber-600">⚠ Cuenta con adeudo — regulariza tu pago</span>
        )}
      </div>

      <div ref={userRef} className="relative">
        <button
          onClick={() => setShowUser((v) => !v)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <UserCircleIcon className="w-7 h-7 text-slate-400" />
          <div className="hidden sm:block text-left">
            <p className="text-sm font-medium text-slate-900 leading-none">{user?.nombre} {user?.apellido}</p>
            <p className="text-xs text-slate-500 mt-0.5 capitalize">{user?.permisos?.admin ? 'Admin' : 'Usuario'}</p>
          </div>
          <ChevronDownIcon className="w-4 h-4 text-slate-400" />
        </button>

        {showUser && (
          <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden z-50 animate-fade-in">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900">{user?.nombre} {user?.apellido}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => { setShowUser(false); setModalPassword(true); }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <KeyIcon className="w-4 h-4" />
              Password
            </button>
            <button
              onClick={salir}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>

      <Modal
        open={modalPassword} onClose={() => setModalPassword(false)} title="Cambio de contraseña" width="max-w-sm"
        footer={(
          <button type="submit" form="form-cambio-password" disabled={guardandoPassword} className="btn-primary">
            {guardandoPassword ? 'Guardando...' : 'Guardar'}
          </button>
        )}
      >
        <form id="form-cambio-password" onSubmit={cambiarPassword} className="space-y-3">
          <div>
            <label className="label">Password Anterior</label>
            <input type="password" required value={passwordActual} onChange={(e) => setPasswordActual(e.target.value)} placeholder="Password Actual" className="input-base w-full" />
          </div>
          <div>
            <label className="label">Password Nuevo</label>
            <input type="password" required minLength={6} value={passwordNuevo} onChange={(e) => setPasswordNuevo(e.target.value)} placeholder="Password Nuevo" className="input-base w-full" />
          </div>
          <div>
            <label className="label">Ingresa Nuevamente</label>
            <input type="password" required value={passwordVerificar} onChange={(e) => setPasswordVerificar(e.target.value)} placeholder="Verificar Password" className="input-base w-full" />
          </div>
        </form>
      </Modal>
    </header>
  );
}
